import { z } from 'zod';
import { FEATURE_KEYS } from './features.js';
import { UploadUrlRequestSchema } from './files.js';
import { InstallHealthSchema } from './integrations.js';
import { PAGE_LIMIT_MAX, paginated, resource } from './pagination.js';
import { PLATFORM_ROLES } from './permissions.js';
import { StateCodeSchema, TimeZoneSchema } from './scheduling.js';
import { BrandingSchema, HexColorSchema, UpdateBrandingRequestSchema } from './tenant-settings.js';
/**
 * The platform (super admin) surface - Docs/API.md §2.13, MASTER_PLAN M12.1-M12.6.
 *
 * The shapes here are the ones `apps/super/src/lib/api/platform.ts` already declares,
 * so wiring the console to the real API is replacing `createMockPlatformClient()` and
 * unwrapping the `{ data }` envelope API.md §1.2 requires. Where this file and that one
 * disagree, the difference is deliberate and listed in `Docs/API.md` §2.13.
 *
 * ## What may never appear in these types
 *
 * No PHI. A super admin legitimately needs to know that a clinic took 412 bookings last
 * month; they have no business reading who those patients were, and a cross-tenant
 * directory is the worst possible place to put it (SECURITY.md §6.1, rules §25). The
 * user list carries an address and a name and nothing else, usage is counts, and every
 * audit `before`/`after` is redacted on the way out.
 */
/* ------------------------------------------------------------------ tenants */
export const TENANT_STATUSES = ['active', 'suspended', 'archived'];
export const TenantStatusSchema = z.enum(TENANT_STATUSES);
export const DOMAIN_STATUSES = ['pending', 'verified', 'active', 'failed'];
export const DomainStatusSchema = z.enum(DOMAIN_STATUSES);
export const DOMAIN_KINDS = ['platform_subdomain', 'custom'];
export const DomainKindSchema = z.enum(DOMAIN_KINDS);
export const DomainAppKindSchema = z.enum(['patient', 'admin']);
/**
 * `ck_tenants_slug`. The same expression the database enforces, so a slug that passes
 * validation cannot then fail an insert.
 */
export const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;
export const TenantSlugSchema = z.string().regex(SLUG_PATTERN, {
    message: 'Use 3-40 lower-case letters, numbers and hyphens.',
});
/*
 * `HEX_COLOR_PATTERN` and `HexColorSchema` moved to `tenant-settings.ts` (M17.1) so this
 * file can reuse `BrandingSchema` without an import cycle; the package still exports
 * both under the same names.
 */
/**
 * Every reason field on this surface. Ten characters is not arbitrary: "test", "fix"
 * and "asdf" all fit in nine, and a reason nobody can read a year later is the same as
 * no reason at all (SECURITY.md §6.2, rules §33.4).
 */
export const ReasonSchema = z
    .string()
    .trim()
    .min(10, { message: 'Say why, in at least 10 characters.' })
    .max(500);
/** Counts behind the health column, so a list row does not need every install. */
export const InstallHealthSummarySchema = z.object({
    healthy: z.number().int().min(0),
    degraded: z.number().int().min(0),
    failed: z.number().int().min(0),
    unconfigured: z.number().int().min(0),
});
export const TenantSummarySchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    name: z.string(),
    status: TenantStatusSchema,
    planId: z.uuid(),
    planKey: z.string(),
    planName: z.string(),
    timeZone: z.string(),
    statesServed: z.array(z.string()),
    domainCount: z.number().int().min(0),
    createdAt: z.iso.datetime(),
    health: InstallHealthSummarySchema,
    /** Why the tenant was suspended. Null while active. */
    suspendedReason: z.string().nullable(),
    suspendedAt: z.iso.datetime().nullable(),
    archivedReason: z.string().nullable(),
    archivedAt: z.iso.datetime().nullable(),
});
/**
 * One `tenant_domains` row, read-only on this surface.
 *
 * Adding, verifying and removing a hostname lives in `apps/api/src/domains/**`; M12
 * only reads the table, so a tenant's detail page can show what resolves to it.
 */
export const TenantDomainSchema = z.object({
    id: z.uuid(),
    host: z.string(),
    appKind: DomainAppKindSchema,
    kind: DomainKindSchema,
    isPrimary: z.boolean(),
    status: DomainStatusSchema,
    verificationToken: z.string().nullable(),
    lastCheckAt: z.iso.datetime().nullable(),
    lastError: z.string().nullable(),
});
/**
 * `tenant_branding` as the super admin edits it: the **same** set the clinic admin
 * edits (`BrandingSchema`, M17.1, ADR-036), including the favicon, the hover / subtle /
 * border colours, the font, the email footer, the read-only signed `logoUrl` /
 * `faviconUrl` and the advisory `contrast` note.
 *
 * An alias rather than a copy: two consoles editing one row through two schemas is how
 * the platform side ended up without a favicon in the first place. `logoFileId` and
 * `faviconFileId` are `files.id`s the tenant owns, uploaded through
 * `POST /platform/tenants/:tenantId/files/upload-url`; `saveBranding` refuses an id that
 * is not this tenant's, is not the right kind, or failed the scan (400).
 */
export const PlatformTenantBrandingSchema = BrandingSchema;
/**
 * `PUT /platform/tenants/:tenantId/branding`. The clinic's own partial
 * (`UpdateBrandingRequestSchema`): every field optional, read-only fields refused.
 */
export const SaveBrandingRequestSchema = UpdateBrandingRequestSchema;
/** The two kinds a platform operator may upload on a tenant's behalf. */
export const PLATFORM_UPLOAD_KINDS = ['logo', 'favicon'];
/**
 * `POST /platform/tenants/:tenantId/files/upload-url`: the clinic route's body with
 * `kind` narrowed to the two branding assets and no `ownerId` (the owner is always the
 * tenant's branding). Any other kind is a `400`: an operator has no business putting a
 * patient document into a clinic.
 */
export const PlatformUploadUrlRequestSchema = UploadUrlRequestSchema.omit({ ownerId: true })
    .extend({ kind: z.enum(PLATFORM_UPLOAD_KINDS) })
    .strict();
/** One feature key for one tenant: the plan's answer, the override, and the effective one. */
export const TenantFeatureSchema = z.object({
    key: z.enum(FEATURE_KEYS),
    planDefault: z.boolean(),
    /** `tenant_features.enabled`, or null when there is no override row (inherit). */
    override: z.boolean().nullable(),
    /** Always `override ?? planDefault`. */
    effective: z.boolean(),
});
/**
 * The one rule both the API and the console must agree on (ARCHITECTURE.md §17):
 * an override wins over the plan in both directions, and no override means inherit.
 */
export function effectiveFeature(planDefault, override) {
    return override ?? planDefault;
}
/** Usage counters for one tenant over a range. Counts only - never rows, never PHI. */
export const TenantUsageSchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    bookings: z.number().int().min(0),
    cancellations: z.number().int().min(0),
    notifications: z.number().int().min(0),
    integrationCalls: z.number().int().min(0),
    integrationErrors: z.number().int().min(0),
    activeProviders: z.number().int().min(0),
    staffUsers: z.number().int().min(0),
});
/** A staff account of one tenant, as the tenant detail page lists it. */
export const TenantUserSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.email(),
    role: z.string(),
    status: z.enum(['active', 'invited', 'deactivated']),
    lastSeenAt: z.iso.datetime().nullable(),
});
/** `GET /platform/tenants/:tenantId`. */
export const TenantDetailSchema = z.object({
    tenant: TenantSummarySchema,
    domains: z.array(TenantDomainSchema),
    branding: PlatformTenantBrandingSchema,
    features: z.array(TenantFeatureSchema),
    installs: InstallHealthSummarySchema,
    users: z.array(TenantUserSchema),
    usage: TenantUsageSchema,
});
export const CreateTenantRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    slug: TenantSlugSchema,
    timeZone: TimeZoneSchema.max(64),
    statesServed: z.array(StateCodeSchema).max(60).default([]),
    planId: z.uuid().optional(),
    /** Invites the first clinic admin when given. No password is ever created here. */
    adminEmail: z.email().optional(),
});
/**
 * `slug` is absent on purpose. The platform subdomain is derived from it and a rename
 * would orphan every link a clinic has already published (API.md §2.13).
 */
export const UpdateTenantRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    /** IANA zone, checked against the runtime's own database (`TimeZoneSchema`). */
    timeZone: TimeZoneSchema.max(64),
    /** Two-letter codes, upper-cased on the way in. */
    statesServed: z.array(StateCodeSchema).max(60),
    planId: z.uuid(),
    retentionYears: z.number().int().min(7).max(25),
    billingNpi: z
        .string()
        .regex(/^\d{10}$/)
        .nullable(),
})
    .partial();
export const SuspendTenantRequestSchema = z.object({
    suspended: z.boolean(),
    reason: ReasonSchema,
});
export const ArchiveTenantRequestSchema = z.object({ reason: ReasonSchema });
export const TenantQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    status: TenantStatusSchema.optional(),
    planId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
    cursor: z.string().min(1).optional(),
});
export const TenantResponseSchema = resource(TenantSummarySchema);
export const TenantListResponseSchema = paginated(TenantSummarySchema);
export const TenantDetailResponseSchema = resource(TenantDetailSchema);
export const TenantBrandingResponseSchema = resource(PlatformTenantBrandingSchema);
/**
 * What `POST /platform/tenants` answers: the tenant plus the admin invitation, if asked
 * for.
 *
 * `inviteUrl` behaves exactly as it does on `POST /admin/invitations` (API.md §2.12):
 * `null` once the invitation has been queued as a `staff_invitation` email, and a link
 * only for a clinic with no enabled email install - which a brand new tenant, minutes
 * old, usually is. Nothing should persist it.
 */
export const CreateTenantResponseSchema = z.object({
    data: z.object({
        tenant: TenantSummarySchema,
        invitation: z
            .object({
            id: z.string(),
            email: z.email(),
            expiresAt: z.iso.datetime(),
            /** Null when the invitation email was queued (M9.x). */
            inviteUrl: z.url().nullable(),
        })
            .nullable(),
    }),
});
/* -------------------------------------------------------------------- plans */
export const PlanFeatureRowSchema = z.object({
    featureKey: z.enum(FEATURE_KEYS),
    enabled: z.boolean(),
    limits: z.record(z.string().max(64), z.number()).default({}),
});
export const PlanSchema = z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    description: z.string(),
    monthlyPriceCents: z.number().int().min(0),
    currency: z.string().length(3),
    isDefault: z.boolean(),
    status: z.enum(['active', 'retired']),
    tenantCount: z.number().int().min(0),
    /** Every key in `FEATURE_KEYS`, so the matrix never has a hole. */
    features: z.record(z.enum(FEATURE_KEYS), z.boolean()),
    limits: z.record(z.string(), z.record(z.string().max(64), z.number())),
});
export const CreatePlanRequestSchema = z.object({
    key: z
        .string()
        .regex(/^[a-z0-9_-]{2,40}$/, { message: 'Use lower-case letters, numbers, _ and -.' }),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(400).default(''),
    monthlyPriceCents: z.number().int().min(0).default(0),
    features: z.array(PlanFeatureRowSchema).default([]),
});
export const UpdatePlanRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(400),
    monthlyPriceCents: z.number().int().min(0),
    isDefault: z.boolean(),
    status: z.enum(['active', 'retired']),
    /** Only the cells given are written; the rest of the matrix is left alone. */
    features: z.array(PlanFeatureRowSchema),
})
    .partial();
export const PlanResponseSchema = resource(PlanSchema);
export const PlanListResponseSchema = z.object({ data: z.array(PlanSchema) });
/** `null` clears the override row, so the tenant inherits the plan again. */
export const SetTenantFeatureRequestSchema = z.object({
    enabled: z.boolean().nullable(),
    config: z.record(z.string().max(64), z.unknown()).optional(),
});
export const TenantFeatureListResponseSchema = z.object({ data: z.array(TenantFeatureSchema) });
/* -------------------------------------------------------------------- users */
export const USER_POPULATIONS = ['patient', 'staff', 'platform'];
export const UserPopulationSchema = z.enum(USER_POPULATIONS);
/**
 * `user.role` from the Better Auth admin plugin. The values are `permissions.ts`'s -
 * a second list here would be a second answer to "what is a super admin".
 */
export const PlatformRoleSchema = z.enum(PLATFORM_ROLES);
export const UserAccountStatusSchema = z.enum(['active', 'invited', 'deactivated']);
export const UserMembershipSchema = z.object({
    tenantId: z.uuid(),
    tenantName: z.string(),
    tenantSlug: z.string(),
    role: z.string(),
});
/** One `session` row. Never the token, and the address only as a hash prefix. */
export const UserSessionRowSchema = z.object({
    id: z.string(),
    userAgent: z.string(),
    /** First 12 characters of the stored hash. Enough to tell two clients apart. */
    ipHash: z.string().nullable(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    /** The session this console is running in, which must not be revoked by accident. */
    isCurrent: z.boolean(),
});
/**
 * One row of `GET /platform/users`.
 *
 * **Patients appear here by address and name and by nothing else.** Everything that
 * makes a patient record a patient record lives behind that tenant's own permissions.
 */
export const PlatformUserSummarySchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string(),
    population: UserPopulationSchema,
    platformRole: PlatformRoleSchema.nullable(),
    memberships: z.array(UserMembershipSchema),
    mfaEnrolled: z.boolean(),
    lastSignInAt: z.iso.datetime().nullable(),
    status: UserAccountStatusSchema,
});
export const PlatformUserDetailSchema = z.object({
    user: PlatformUserSummarySchema,
    sessions: z.array(UserSessionRowSchema),
    mfa: z.object({
        enrolled: z.boolean(),
        /** `totp`, `passkey`, `backup_codes` - names, never secrets. */
        methods: z.array(z.string()),
        lastVerifiedAt: z.iso.datetime().nullable(),
    }),
});
export const PlatformUserQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    population: UserPopulationSchema.optional(),
    tenantId: z.uuid().optional(),
    status: UserAccountStatusSchema.optional(),
    mfa: z.enum(['enrolled', 'missing']).optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
    cursor: z.string().min(1).optional(),
});
/** An invitation, never an account with a password. */
export const InvitePlatformUserRequestSchema = z.object({
    email: z.email(),
    name: z.string().trim().min(1).max(120),
    role: PlatformRoleSchema,
});
export const SetUserActiveRequestSchema = z.object({
    active: z.boolean(),
    reason: ReasonSchema,
});
export const ResetMfaRequestSchema = z.object({ reason: ReasonSchema });
/**
 * The phrase a promotion or demotion asks the operator to type (SECURITY.md §6.2).
 *
 * It is the target's own address rather than a fixed word like "PROMOTE": a fixed word
 * is muscle memory after the second time, and the mistake this control exists to catch
 * is promoting the *wrong person*.
 */
export function confirmationPhraseFor(user) {
    return user.email;
}
export const PromoteUserRequestSchema = z.object({
    role: PlatformRoleSchema,
    reason: ReasonSchema,
    /** The target's email address, typed by the operator (`confirmationPhraseFor`). */
    confirmation: z.string().trim().min(1),
});
export const DemoteUserRequestSchema = z.object({
    reason: ReasonSchema,
    confirmation: z.string().trim().min(1),
});
export const ConfirmPromotionRequestSchema = z.object({ promotionId: z.uuid() });
/**
 * A promotion awaiting a second administrator (`platform_promotions`).
 *
 * `platform_support` is granted immediately; `platform_admin` is not, because the role
 * can promote further admins and a single compromised console would otherwise be the
 * whole platform (SECURITY.md §6.2, API.md §2.13).
 */
export const PendingPromotionSchema = z.object({
    id: z.uuid(),
    targetUserId: z.string(),
    role: PlatformRoleSchema,
    requestedBy: z.string(),
    requestedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const PlatformUserResponseSchema = resource(PlatformUserSummarySchema);
export const PlatformUserListResponseSchema = paginated(PlatformUserSummarySchema);
export const PlatformUserDetailResponseSchema = resource(PlatformUserDetailSchema);
/** Promotion answers carry the user *and* whether a second approval is still needed. */
export const PromotionResponseSchema = z.object({
    data: z.object({
        user: PlatformUserSummarySchema,
        pending: PendingPromotionSchema.nullable(),
    }),
});
/** A `platform_promotions` row's lifecycle, as the column spells it. */
export const PromotionStatusSchema = z.enum(['pending', 'confirmed', 'cancelled', 'expired']);
/**
 * `GET /platform/promotions`.
 *
 * The queue a *second* administrator reads. Without it the two-person rule is only half
 * built: the first admin gets a promotion id in their own response body and the second
 * has no way to discover it short of being told over chat, which is exactly the
 * out-of-band channel SECURITY.md §6.2 is trying to replace.
 *
 * `status` defaults to `pending` because that is the only value that is a to-do list;
 * the others are history and are there so a console can show what happened to a request.
 */
export const PlatformPromotionQuerySchema = z.object({
    status: PromotionStatusSchema.default('pending'),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
    cursor: z.string().min(1).optional(),
});
/**
 * One row of that queue.
 *
 * The requester and the target are carried as name and address so the second
 * administrator can recognise **who** they are about to make a platform admin without a
 * second round trip per row - which is the whole point of the confirmation phrase being
 * the target's own address.
 */
export const PendingPromotionRowSchema = PendingPromotionSchema.extend({
    status: PromotionStatusSchema,
    /** Why the first administrator asked. At least 10 characters, no PHI by policy. */
    reason: z.string(),
    requestedByName: z.string().nullable(),
    requestedByEmail: z.string().nullable(),
    targetName: z.string().nullable(),
    targetEmail: z.string().nullable(),
});
export const PlatformPromotionListResponseSchema = paginated(PendingPromotionRowSchema);
/* --------------------------------------------------------------------- logs */
/** One `audit_log` row, redacted by the API before it is sent. */
export const PlatformAuditRowSchema = z.object({
    id: z.uuid(),
    tenantId: z.uuid().nullable(),
    tenantName: z.string().nullable(),
    actorId: z.string().nullable(),
    actorLabel: z.string(),
    actorRole: z.string().nullable(),
    impersonatorLabel: z.string().nullable(),
    action: z.string(),
    tableName: z.string(),
    rowId: z.uuid().nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    correlationId: z.string().nullable(),
    occurredAt: z.iso.datetime(),
});
export const PlatformAuditQuerySchema = z.object({
    tenantId: z.uuid().optional(),
    actorId: z.string().max(120).optional(),
    action: z.string().max(120).optional(),
    tableName: z.string().max(120).optional(),
    rowId: z.uuid().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
    cursor: z.string().min(1).optional(),
});
export const PlatformAuditListResponseSchema = paginated(PlatformAuditRowSchema);
/**
 * Field names whose value is never rendered in a before/after diff, whatever was
 * stored (SECURITY.md §7.4, rules §32.5-7, FR-LOG-01).
 *
 * `audit_redact()` scrubs on the way in and this scrubs on the way out: an audit row is
 * written by a hundred call sites, and the one that forgets must not be able to paint a
 * token onto a support engineer's screen.
 */
export const SENSITIVE_FIELD_PATTERN = /pass|secret|token|credential|(^|_)cred_|api_?key|signature|ssn|dob|birth|otp|dek_wrapped|_enc$|_hash$|code$/i;
export const AUDIT_REDACTED = '[redacted]';
export function isSensitiveField(key) {
    return SENSITIVE_FIELD_PATTERN.test(key);
}
/** Replaces every sensitive value in one side of a diff. Shape is preserved. */
export function redactAuditPayload(payload) {
    if (payload === null)
        return null;
    return Object.fromEntries(Object.entries(payload).map(([key, value]) => [
        key,
        isSensitiveField(key) ? AUDIT_REDACTED : value,
    ]));
}
/** One pg-boss queue (ADR-006, ARCHITECTURE.md §14). */
export const QueueSummarySchema = z.object({
    name: z.string(),
    /** Jobs waiting to be taken (`created` plus `retry`). */
    depth: z.number().int().min(0),
    created: z.number().int().min(0),
    retry: z.number().int().min(0),
    active: z.number().int().min(0),
    failed: z.number().int().min(0),
    /** Jobs that exhausted their retries and landed in `<queue>__dlq` (rules §30.5). */
    deadLettered: z.number().int().min(0),
    oldestPendingAt: z.iso.datetime().nullable(),
});
export const QueueSummaryListResponseSchema = z.object({ data: z.array(QueueSummarySchema) });
/** One dead-lettered job, with enough to choose between redrive and discard. */
export const DeadLetterJobSchema = z.object({
    id: z.uuid(),
    /** The dead-letter queue the job is sitting in (`payments__dlq`). */
    queue: z.string(),
    /** The source queue it failed on (`payments`). */
    sourceQueue: z.string(),
    jobName: z.string(),
    tenantId: z.uuid().nullable(),
    errorClass: z.string(),
    /** Already redacted by the API; never a payload. */
    errorMessage: z.string(),
    attempts: z.number().int().min(0),
    firstFailedAt: z.iso.datetime(),
    lastFailedAt: z.iso.datetime(),
});
export const DeadLetterQuerySchema = z.object({
    queue: z.string().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
});
export const DeadLetterListResponseSchema = z.object({ data: z.array(DeadLetterJobSchema) });
export const DiscardJobRequestSchema = z.object({ reason: ReasonSchema });
export const RedriveJobRequestSchema = z.object({ reason: ReasonSchema });
/** The window a request summary covers. Dashboard metrics name their range (rules §51.7). */
export const REQUEST_RANGES = ['24h', '7d', '30d'];
export const RequestRangeSchema = z.enum(REQUEST_RANGES);
export const RequestSummaryRowSchema = z.object({
    route: z.string(),
    method: z.string(),
    surface: z.string(),
    total: z.number().int().min(0),
    ok: z.number().int().min(0),
    clientError: z.number().int().min(0),
    serverError: z.number().int().min(0),
    p50Ms: z.number().min(0),
    p95Ms: z.number().min(0),
});
export const RequestSummarySchema = z.object({
    range: RequestRangeSchema,
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    total: z.number().int().min(0),
    /** Share of requests that answered 5xx, 0-1. */
    errorRate: z.number().min(0).max(1),
    p50Ms: z.number().min(0),
    p95Ms: z.number().min(0),
    rows: z.array(RequestSummaryRowSchema),
});
export const RequestSummaryQuerySchema = z.object({
    range: RequestRangeSchema.default('24h'),
    tenantId: z.uuid().optional(),
});
export const RequestSummaryResponseSchema = resource(RequestSummarySchema);
/**
 * One error group. Grouped by `(surface, route, status)` from `request_logs` plus the
 * error code carried on the matching `event.*` audit rows - Sentry is the other half
 * (ADR-020) and `url` stays null until a Sentry org is configured.
 */
export const ErrorGroupSchema = z.object({
    id: z.string(),
    code: z.string(),
    message: z.string(),
    surface: z.string(),
    route: z.string(),
    status: z.number().int(),
    count: z.number().int().min(0),
    tenantCount: z.number().int().min(0),
    lastSeenAt: z.iso.datetime(),
    url: z.url().nullable(),
});
export const ErrorGroupListResponseSchema = z.object({
    data: z.array(ErrorGroupSchema),
    range: RequestRangeSchema,
});
/* ------------------------------------------------------------------ reports */
export const TenantUsageRowSchema = TenantUsageSchema.extend({
    tenantId: z.uuid(),
    tenantName: z.string(),
    tenantSlug: z.string(),
    planKey: z.string(),
    status: TenantStatusSchema,
});
export const UsageReportQuerySchema = z.object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    tenantId: z.uuid().optional(),
});
export const UsageReportResponseSchema = z.object({
    data: z.object({
        range: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
        tenants: z.array(TenantUsageRowSchema),
    }),
});
/* ------------------------------------------------------------------- health */
/** The Overview card (FR-SUPER-05). One number per thing that wakes somebody up. */
export const PlatformHealthSchema = z.object({
    database: z.enum(['ok', 'failed']),
    queue: z.enum(['ok', 'failed', 'skipped']),
    queueDepth: z.number().int().min(0),
    queuesFailed: z.number().int().min(0),
    deadLetters: z.number().int().min(0),
    /** Installs whose last health check failed, across every tenant. */
    failedInstalls: z.number().int().min(0),
    degradedInstalls: z.number().int().min(0),
    suspendedTenants: z.number().int().min(0),
    maintenanceMode: z.boolean(),
    checkedAt: z.iso.datetime(),
});
export const PlatformHealthResponseSchema = resource(PlatformHealthSchema);
export const TenantHealthRowSchema = z.object({
    tenantId: z.uuid(),
    tenantName: z.string(),
    tenantSlug: z.string(),
    status: TenantStatusSchema,
    installs: z.array(z.object({ key: z.string(), health: InstallHealthSchema })),
    failedJobs: z.number().int().min(0),
});
export const TenantHealthListResponseSchema = z.object({ data: z.array(TenantHealthRowSchema) });
/* ----------------------------------------------------------------- settings */
export const NotificationTemplateDefaultSchema = z.object({
    key: z.string().max(80),
    name: z.string().max(120),
    channel: z.enum(['email', 'sms']),
});
/** The three sentences a patient reads about money. None of them is a quote. */
export const PricingPresentationTextsSchema = z.object({
    selfPay: z.string().trim().min(1).max(300),
    copayEstimate: z.string().trim().min(1).max(300),
    pendingReview: z.string().trim().min(1).max(300),
});
/** The variables each presentation text may use. Anything else is refused on save. */
export const PRICING_TEXT_VARIABLES = {
    selfPay: ['{{amount}}', '{{clinicName}}', '{{appointmentType}}'],
    copayEstimate: ['{{amount}}', '{{payerName}}', '{{clinicName}}'],
    pendingReview: ['{{clinicName}}', '{{supportPhone}}'],
};
/** ADR-021: seven years is the floor, and a longer hold is a deliberate choice. */
export const RETENTION_MIN_YEARS = 7;
export const RETENTION_MAX_YEARS = 25;
export const MaintenanceModeSchema = z.object({
    enabled: z.boolean(),
    message: z.string().trim().max(300),
});
export const PlatformSettingsSchema = z.object({
    /** Read-only: the API reads it from `PLATFORM_DOMAIN` at boot (ENVIRONMENT.md §2). */
    platformDomain: z.string(),
    /** Read-only: what a tenant's custom domain CNAMEs at. */
    cnameTarget: z.string(),
    defaultTemplates: z.array(NotificationTemplateDefaultSchema),
    pricingTexts: PricingPresentationTextsSchema,
    /** Years. A tenant may lengthen it, never shorten it (ADR-021). */
    retentionYears: z.number().int().min(RETENTION_MIN_YEARS).max(RETENTION_MAX_YEARS),
    /** The plan a tenant created without an explicit one is put on. */
    defaultPlanKey: z.string().nullable(),
    defaultTimeZone: z.string(),
    defaultBranding: z.object({
        primaryColor: HexColorSchema.nullable(),
        secondaryColor: HexColorSchema.nullable(),
        accentColor: HexColorSchema.nullable(),
    }),
    maintenanceMode: MaintenanceModeSchema,
    sentryUrl: z.url().nullable(),
    monitorUrl: z.url().nullable(),
    contacts: z.array(z.object({ name: z.string(), role: z.string(), email: z.email() })),
});
/** The editable subset. The domain, the CNAME target and the template list are not. */
export const PlatformSettingsPatchSchema = z
    .object({
    pricingTexts: PricingPresentationTextsSchema,
    retentionYears: z.number().int().min(RETENTION_MIN_YEARS).max(RETENTION_MAX_YEARS),
    defaultPlanKey: z.string().max(40).nullable(),
    defaultTimeZone: z.string().min(1).max(64),
    defaultBranding: z.object({
        primaryColor: HexColorSchema.nullable(),
        secondaryColor: HexColorSchema.nullable(),
        accentColor: HexColorSchema.nullable(),
    }),
    maintenanceMode: MaintenanceModeSchema,
    sentryUrl: z.url().nullable(),
    monitorUrl: z.url().nullable(),
    contacts: z.array(z.object({ name: z.string(), role: z.string(), email: z.email() })),
})
    .partial()
    .refine((patch) => patch.maintenanceMode === undefined ||
    !patch.maintenanceMode.enabled ||
    patch.maintenanceMode.message.trim().length >= 10, {
    message: 'Say what is happening and when it ends. At least 10 characters.',
    path: ['maintenanceMode', 'message'],
})
    .refine((patch) => pricingTextProblem(patch.pricingTexts) === null, {
    message: 'A presentation text uses a variable that is not available here.',
    path: ['pricingTexts'],
});
/**
 * Which presentation text uses a variable it may not, or null when all of them are
 * fine. Exported so the console can put the message under the right control.
 */
export function pricingTextProblem(texts) {
    if (texts === undefined)
        return null;
    for (const field of Object.keys(PRICING_TEXT_VARIABLES)) {
        const allowed = PRICING_TEXT_VARIABLES[field];
        for (const used of texts[field].match(/\{\{[^}]*\}\}/g) ?? []) {
            if (!allowed.includes(used))
                return { field, variable: used };
        }
    }
    return null;
}
export const PlatformSettingsResponseSchema = resource(PlatformSettingsSchema);
/* ------------------------------------------------------------ impersonation */
/**
 * `DELETE /platform/impersonation/current`.
 *
 * The exit answers with somewhere to go, exactly as the start route does: the operator
 * pressed a button inside the clinic's admin app and their own console is on another
 * host, so a 204 would leave the browser sitting on a page it no longer has a session
 * for (HANDOFF, backend follow-ups).
 */
export const EndImpersonationResponseSchema = z.object({ redirectUrl: z.url() });
/* ------------------------------------------------------------ shared labels */
export const PLATFORM_ROLE_LABELS = {
    platform_support: 'Platform support',
    platform_admin: 'Platform admin',
};
export const POPULATION_LABELS = {
    patient: 'Patient',
    staff: 'Clinic staff',
    platform: 'Object Labs',
};
export const REQUEST_RANGE_LABELS = {
    '24h': 'Last 24 hours',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
};
/** How far back a range reaches, in milliseconds. */
export const REQUEST_RANGE_MS = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
};
/** Derives a candidate slug from a clinic name, the way the New tenant dialog does. */
export function slugify(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
}
//# sourceMappingURL=platform.js.map